import { useMemo } from 'react';
import { prop } from 'ramda';
import { useForm } from 'react-final-form';
import { TFormField } from 'js/components/common/Form/types';
import { FORM_FIELD_TYPE, FORM_VALIDATION_RULE_TYPE } from 'js/components/common/Form/constants';

import { useValidatePhone } from './useValidatePhone';
import { useValidateEmail } from './useValidateEmail';
import { useDynamicReducerSelector } from '../../../store';
import { useUpdateFieldValue } from './useUpdateFieldValue';
import { useIBANFieldValidation } from './useIBANFieldValidation';
import { useValidateUserName } from './useUserNameFieldValidation';
import { IUseValidatePhoneOptions } from './useValidatePhone/types';
import { useAddressLookupValidation } from './useAddressLookupValidation';
import { usePolandIdFieldValidation } from './usePolandIdFieldValidation';
import { useItalianIdFieldValidation } from './useItalianIdFieldValidation';
import { useSpanishIdFieldValidation } from './useSpanishIdFieldValidation';
import { usePortugalIdFieldValidation } from './usePortugalIdFieldValidation';
import { useRomanianIdFieldValidation } from './useRomanianIdFieldValidation';
import { useWithRecordLevelValidation } from './useWithRecordLevelValidation';
import { useSouthAfricanIdFieldValidation } from './useSouthAfricanIdFieldValidation';

export const useInternalValidate = ({
    field,
    requestReCaptchaToken,
    abortReCaptchaTokenRequest
}: {
    field: TFormField;
    abortReCaptchaTokenRequest: () => Promise<void>;
    requestReCaptchaToken: () => Promise<{ token: string }>;
}) => {
    const formAPI = useForm();
    const hiddenByRulesFields = useDynamicReducerSelector(prop('hiddenByRulesFields'));

    const { name: fieldName, type, validation, validationRules, readOnly } = field;
    const isFieldWithInternalValidation = validation
        && !readOnly
        && validationRules?.some(rule => rule.type === FORM_VALIDATION_RULE_TYPE.internal);
    const isPasswordWithExclusions = validation
        && validationRules?.some(rule => rule.type === FORM_VALIDATION_RULE_TYPE.exclusion);

    const updateFieldValue = useUpdateFieldValue({ fieldName });
    const withRecordLevelValidation = useWithRecordLevelValidation({ fieldName });

    const validateAddressLookup = useAddressLookupValidation({ field });

    const validateUserName = useValidateUserName({
        fieldName,
        updateFieldValue,
        requestReCaptchaToken,
        abortReCaptchaTokenRequest
    });

    const validateEmail = useValidateEmail({
        fieldName,
        requestReCaptchaToken,
        abortReCaptchaTokenRequest
    });

    const validateItalianId = useItalianIdFieldValidation({ fieldName });

    const validatePolandId = usePolandIdFieldValidation({ fieldName });

    const validateRomanianId = useRomanianIdFieldValidation({ fieldName });

    const validateSouthAfricanId = useSouthAfricanIdFieldValidation({ fieldName });

    const validatePortugalId = usePortugalIdFieldValidation({ fieldName });

    const validateSpanishId = useSpanishIdFieldValidation({ fieldName });

    const validateIBAN = useIBANFieldValidation({ fieldName });

    const validatePhone = useValidatePhone(<IUseValidatePhoneOptions>{
        field,
        requestReCaptchaToken,
        abortReCaptchaTokenRequest
    });

    // when fields from password exclusions has async validation:
    // RFF incorrectly handles password's record-level validation but field-level validation is run ok
    const validatePassword = useMemo(() => withRecordLevelValidation(async () => {}), [withRecordLevelValidation]);

    const isRegistered = formAPI.getRegisteredFields().includes(fieldName);

    return useMemo(() => {
        const isFieldHidden = hiddenByRulesFields.includes(fieldName);

        if (!(isFieldWithInternalValidation || isPasswordWithExclusions) || !isRegistered || isFieldHidden) {
            return;
        }

        switch (type) {
            case FORM_FIELD_TYPE.ADDRESS_LOOKUP:
            case FORM_FIELD_TYPE.ADDRESS_LOOKUP_SWISS:
                return validateAddressLookup;
            case FORM_FIELD_TYPE.EMAIL:
                return validateEmail;
            case FORM_FIELD_TYPE.USER_NAME:
                return validateUserName;
            case FORM_FIELD_TYPE.ITALIAN_ID:
                return validateItalianId;
            case FORM_FIELD_TYPE.POLAND_ID:
                return validatePolandId;
            case FORM_FIELD_TYPE.ROMANIAN_ID:
                return validateRomanianId;
            case FORM_FIELD_TYPE.SOUTH_AFRICAN_ID:
                return validateSouthAfricanId;
            case FORM_FIELD_TYPE.PORTUGAL_ID:
                return validatePortugalId;
            case FORM_FIELD_TYPE.SPANISH_ID:
                return validateSpanishId;
            case FORM_FIELD_TYPE.IBAN:
                return validateIBAN;
            case FORM_FIELD_TYPE.PHONE:
            case FORM_FIELD_TYPE.CELLPHONE:
                return validatePhone;
            case FORM_FIELD_TYPE.PASSWORD:
                return validatePassword;
            default:
                break;
        }
    }, [
        fieldName,
        hiddenByRulesFields,
        isFieldWithInternalValidation,
        isPasswordWithExclusions,
        isRegistered,
        type,
        validateAddressLookup,
        validateEmail,
        validateIBAN,
        validateItalianId,
        validatePassword,
        validatePhone,
        validatePolandId,
        validatePortugalId,
        validateRomanianId,
        validateSouthAfricanId,
        validateSpanishId,
        validateUserName
    ]);
};
